import { Divider, Form, Grid, Modal, Space, theme, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useInvitationRequest } from './hooks/useInvitationRequest';
import ReactDOM from 'react-dom';
import AntLayout from 'antd/es/layout';
import {
  getInvitationStatus,
  GuestItem,
  useFetchHost,
  useFetchWaitingList,
  useFetchWaitingListSurveyInstances,
  useGuestListStore,
  useReserveGuestListStore,
} from '@seaters/data-access';
import {} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  GuestEntity,
  ImportanceEnum,
  InvitationApprovalMode,
  InvitationEntity,
  InvitationsListEntity,
  InvitationStatusEnum,
  SurveyExtensionPoint,
  WLPositionStatus,
} from '@seaters/constants';
import { Button } from '@seaters/ui';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useEffect, useMemo, useState, ReactNode } from 'react';
import TextArea from 'antd/es/input/TextArea';
import './styles.css';
import { InvitationListTable } from './components/InvitationListTable';
import { useAcceptInvitation, useDeclineInvitation } from './hooks';
import { PublicOfficialModal } from '../PublicOfficialModal';
import { InternalEmployeeModal } from '../InternalEmployeeModal';
import { CheckoutModalHost } from '../CheckoutModal/CheckoutModalHost';
import { EditContactModal } from '../ContactList/EditContactModal';
import { useEditContact } from '../../utils/useEditContact';
import { useGuestInvitation } from './helpers/guestInvitation';

const { Footer: AntFooter } = AntLayout;

const { Text } = Typography;

const { useBreakpoint } = Grid;

dayjs.extend(relativeTime);

const statusesToIgnoreTickets = [
  InvitationStatusEnum.CANCELLED,
  InvitationStatusEnum.RSVP_DECLINED,
  InvitationStatusEnum.DEMAND_REFUSED,
];

export function InvitationList({
  waitingListId,
  invitations,
  replaceGuestFromReserveToMain,
  isFetching,
  renderFeedbackCell,
  renderBudgetCell,
}: {
  waitingListId: string;
  invitations: InvitationsListEntity;
  replaceGuestFromReserveToMain?: (
    reservedGuests: GuestItem[],
    allocatedSeats?: number
  ) => void;
  isFetching?: boolean;
  // PROTOTYPE (invite-management thread A): optional per-row cell renderers,
  // supplied by the app layer. Feedback = organiser reply/revise; budget = the
  // compliance/cap indicator shown alongside Cost/Contact YTD.
  renderFeedbackCell?: (invitation: InvitationEntity | undefined) => ReactNode;
  renderBudgetCell?: (invitation: InvitationEntity | undefined) => ReactNode;
}) {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const { token } = theme.useToken();

  const { data: surveyInstancesAtCheckout } =
    useFetchWaitingListSurveyInstances({
      waitinglist_id: waitingListId,
      extension_point: SurveyExtensionPoint.AT_CHECKOUT,
    });

  const { data: surveyInstancesBeforePayment } =
    useFetchWaitingListSurveyInstances({
      waitinglist_id: waitingListId,
      extension_point: SurveyExtensionPoint.BEFORE_PAYMENT,
    });

  const surveyInstances = surveyInstancesAtCheckout?.content.length
    ? surveyInstancesAtCheckout
    : surveyInstancesBeforePayment;

  const defaultTotalTickets = invitations.content
    .filter((invitation) => {
      const status = getInvitationStatus(invitation);

      return (
        status &&
        !statusesToIgnoreTickets.includes(status) &&
        invitation.importance === ImportanceEnum.REQUIRED
      );
    })
    .map((invitation) => {
      return (
        invitation.nbrOfRequestedSeats || invitation.numberOfNonAllocatedSeats
      );
    })
    .reduce((accumulator, currentValue) => accumulator + currentValue, 0);

  const totalTicketsToIgnore = invitations.content
    .filter((invitation) => {
      const status = getInvitationStatus(invitation);

      return status && statusesToIgnoreTickets.includes(status);
    })
    .map(
      (invitation) =>
        invitation.nbrOfRequestedSeats || invitation.numberOfNonAllocatedSeats
    )
    .reduce((accumulator, currentValue) => accumulator + currentValue, 0);

  const [specialGuestModal, setSpecialGuestModal] = useState<boolean>(false);

  const [businessReason, setBusinessReason] = useState<string>();
  const [totalTicketsChange, setTotalTicketsChange] = useState<boolean>(false);
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [publicOfficialModal, setPublicOfficialModal] =
    useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  const [businessReasonModal, setBusinessReasonModal] = useState(false);

  const [emptyBusinessReasonModal, setEmptyBusinessReasonModal] =
    useState(false);

  const [checkoutModalOpened, setCheckoutModalOpened] =
    useState<boolean>(false);
  const [autoRequestPending, setAutoRequestPending] = useState(false);

  const [selectedGuest, setSelectedGuest] = useState<InvitationEntity | null>(
    null
  );

  const { data: host } = useFetchHost();

  const { data: waitingList } = useFetchWaitingList(waitingListId);
  const maxTicketsPerHost =
    waitingList && waitingList.maxNumberOfSeatsPerHost > 0
      ? waitingList.maxNumberOfSeatsPerHost
      : undefined;

  // *** Guest list store

  const setTickets = useGuestListStore((state) => state.setTickets);
  const addGuest = useGuestListStore((state) => state.add);
  const addAllGuests = useGuestListStore((state) => state.addAll);
  const reset = useGuestListStore((state) => state.reset);
  const guests = useGuestListStore((state) => state.guests);
  const totalTickets = useGuestListStore((state) => state.totalTickets);
  const setTotalTickets = useGuestListStore((state) => state.setTotalTickets);
  const removeGuest = useGuestListStore((state) => state.remove);

  // ***

  // *** Reserve guest list store

  const addReserveGuests = useReserveGuestListStore((state) => state.addAll);
  const reserveGuestMap = useReserveGuestListStore((state) => state.guests);
  const removeReserveGuest = useReserveGuestListStore((state) => state.remove);

  // ***

  const groupGuests = useMemo(() => {
    const nextGroupGuests = Object.values(guests);
    const hostGuestIndex = nextGroupGuests.findIndex(
      (guest) => guest.guest.guestId === host?.userId
    );

    if (hostGuestIndex > 0) {
      nextGroupGuests.splice(
        0,
        0,
        nextGroupGuests.splice(hostGuestIndex, 1)[0]
      );
    }

    const allocatedIndex = nextGroupGuests.findIndex(
      (guest) => guest.isToAllocated
    );

    if (allocatedIndex > 0) {
      nextGroupGuests.splice(
        hostGuestIndex + 1,
        0,
        nextGroupGuests.splice(allocatedIndex, 1)[0]
      );
    }

    return nextGroupGuests;
  }, [guests, host?.userId]);

  const reserveGuests = useMemo(
    () => Object.values(reserveGuestMap),
    [reserveGuestMap]
  );

  const guestInvitation = useGuestInvitation();

  const nonAllocatedGuests = useMemo(() => {
    return groupGuests.filter((guest) => {
      return !guest?.isToAllocated;
    });
  }, [groupGuests]);

  const nonAllocatedTickets = nonAllocatedGuests
    .map((el) => {
      const invitation = invitations.content.find(
        (inv) => inv.guest.guestId === el.guest.guestId
      );
      const status = getInvitationStatus(invitation);
      if (
        (invitation &&
          ((invitation?.status === 'APPROVED' &&
            (!invitation.position ||
              invitation.position.seatsRequestStatus === 'DECLINED')) ||
            invitation?.status === 'CANCELLED' ||
            (status && statusesToIgnoreTickets.includes(status)))) ||
        invitation?.importance === ImportanceEnum.OPTIONAL
      ) {
        return 0;
      }
      return el.tickets;
    })
    .reduce((accumulator, currentValue) => accumulator + currentValue, 0);

  const allocatedInvitation = useMemo(() => {
    return groupGuests.find((guest) => guest.isToAllocated);
  }, [groupGuests]);

  const getRemainingTickets = () => {
    const remaining = Math.max(
      0,
      totalTickets - nonAllocatedTicketsWithOptional
    );
    return remaining;
  };

  const setAllocatedTickets = (tickets: number) => {
    const invitation = guestInvitation(
      allocatedInvitation?.guest.guestId ?? ''
    );
    const status = getInvitationStatus(invitation);
    if (allocatedInvitation) {
      if (status) {
        if (!statusesToIgnoreTickets.includes(status)) {
          setTickets(allocatedInvitation.guest.guestId, tickets);
        }
      } else {
        setTickets(allocatedInvitation.guest.guestId, tickets);
      }
    }
  };

  const reserveListTotal = reserveGuests.length;
  const { handleRequest, handleTotalTickets, submitRequest } =
    useInvitationRequest({
      waitingListId,
      invitations,
      groupGuests,
      reserveGuests,
      allocatedInvitation,
      guestInvitation,
      host,
      waitingList,
      businessReason,
      maxTicketsPerHost,
      setTotalTickets,
      setLoading,
      setSpecialGuestModal,
      setConfirmationModal,
      setBusinessReason,
      setEmptyBusinessReasonModal,
    });

  const [form] = Form.useForm();

  const handleDeclineInvitation = useDeclineInvitation(waitingListId);
  const handleAcceptInvitation = useAcceptInvitation(waitingListId);

  const {
    isEditContactModal,
    setIsEditContactModal,
    showEditModal,
    guestToEdit,
    setGuestToEdit,
  } = useEditContact();

  const showBusinessReasonModal = () => {
    setBusinessReasonModal(true);
  };

  const hideBusinessReasonModal = () => {
    setBusinessReasonModal(false);
  };

  // Get assignation mode from waiting list (top-level property)
  const assignationMode = waitingList?.assignationMode;

  const handleRemoveGuest = (guest: GuestEntity, tickets: number) => {
    removeGuest(guest.guestId);

    if (reserveGuests.length) {
      const guestToMove = reserveGuests[0];
      removeReserveGuest(guestToMove.guest.guestId);
      addGuest(guestToMove.guest, tickets);
    }
    if (
      allocatedInvitation &&
      guest.guestId !== allocatedInvitation.guest.guestId
    ) {
      setTickets(
        allocatedInvitation.guest.guestId,
        (allocatedInvitation.tickets ?? 0) + tickets
      );
    }
  };

  const showChangeTickets = () => {
    setTotalTicketsChange(true);
  };

  const hideChangeTickets = () => {
    setTotalTicketsChange(false);
  };

  const nonAllocatedTicketsWithOptional = nonAllocatedGuests
    .map((el) => {
      const invitation = invitations.content.find(
        (inv) => inv.guest.guestId === el.guest.guestId
      );
      const status = getInvitationStatus(invitation);

      if (
        invitation &&
        ((invitation?.status === 'APPROVED' &&
          (!invitation.position ||
            invitation.position.seatsRequestStatus === 'DECLINED')) ||
          invitation?.status === 'CANCELLED' ||
          (status && statusesToIgnoreTickets.includes(status)))
      ) {
        return 0;
      }
      return el.tickets;
    })
    .reduce((accumulator, currentValue) => accumulator + currentValue, 0);

  const handleTotalTicketsSubmit = () => {
    const allocatedSeats = totalTickets - nonAllocatedTicketsWithOptional;
    if (allocatedInvitation && waitingList) {
      setAllocatedTickets(allocatedSeats);
    }

    if (!allocatedInvitation && allocatedSeats > 0) {
      addGuest(
        {
          guestId: 'to_allocated_id',
          firstName: 'string',
          customInfos: [],
          guestEmail: '',
          mobileCountryCallingCode: '',
          mobileLocalNumber: '',
          lang: '',
          lastName: '',
        },
        allocatedSeats,
        true
      );
    }

    setAllocatedTickets(allocatedSeats);

    if (
      allocatedSeats > 0 &&
      reserveGuests.length &&
      replaceGuestFromReserveToMain
    ) {
      replaceGuestFromReserveToMain(reserveGuests, allocatedSeats);
    }

    hideChangeTickets();

    if (
      waitingList?.invitationParameters?.approvalMode ===
      InvitationApprovalMode.NONE
    ) {
      setAutoRequestPending(true);
    }
  };

  const handlePublicOfficialModal = () => {
    setPublicOfficialModal(true);
  };

  const confirmAcceptInvitation = (invitation: InvitationEntity) => {
    if (
      waitingList?.eventRequiredAttendeeInfo ||
      !!surveyInstances?.content.length
    ) {
      setSelectedGuest(invitation);
      setCheckoutModalOpened(true);
    } else {
      handleAcceptInvitation(invitation.id);
    }
  };

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  useEffect(() => {
    setBusinessReason(
      invitations.content.find((item) => !!item.numberOfNonAllocatedSeats)
        ?.invitationReason
    );
  }, [invitations.content.length]);

  useEffect(() => {
    const allGuests: { [guestId: string]: GuestItem } = {};
    const reserveGuests: { [guestId: string]: GuestItem } = {};

    const sortedInvitations = invitations.content.sort(
      (a, b) => a.order - b.order
    );

    const mainInvitations = sortedInvitations.filter(
      (el) => el.importance === ImportanceEnum.REQUIRED
    );

    const reserveInvitations = sortedInvitations.filter(
      (el) => el.importance === ImportanceEnum.OPTIONAL
    );

    mainInvitations.forEach((invitation) => {
      if (invitation.numberOfNonAllocatedSeats) {
        allGuests[invitation.guest.guestId] = {
          guest: invitation.guest,
          tickets: invitation.numberOfNonAllocatedSeats,
          isToAllocated: true,
          order: invitation.order,
        };
      } else {
        allGuests[invitation.guest.guestId] = {
          guest: invitation.guest,
          tickets: invitation.nbrOfRequestedSeats,
          order: invitation.order,
        };
      }
    });
    addAllGuests(allGuests);

    reserveInvitations.forEach((invitation) => {
      if (invitation.numberOfNonAllocatedSeats) {
        reserveGuests[invitation.guest.guestId] = {
          guest: invitation.guest,
          tickets: invitation.numberOfNonAllocatedSeats,
          isToAllocated: true,
          order: invitation.order,
        };
      } else {
        reserveGuests[invitation.guest.guestId] = {
          guest: invitation.guest,
          tickets: invitation.nbrOfRequestedSeats,
          order: invitation.order,
        };
      }
    });

    addReserveGuests(reserveGuests);

    handleTotalTickets(defaultTotalTickets);
  }, [invitations]);

  useEffect(() => {
    if (maxTicketsPerHost === undefined || totalTickets <= maxTicketsPerHost) {
      return;
    }

    handleTotalTickets(totalTickets);
  }, [handleTotalTickets, maxTicketsPerHost, totalTickets]);

  useEffect(() => {
    if (!autoRequestPending) {
      return;
    }

    // Wait for the ticket updates above to flush, then go through the normal
    // request flow so validation and confirmation are not skipped.
    setAutoRequestPending(false);
    handleRequest();
  }, [autoRequestPending, handleRequest]);

  if (!waitingList) {
    return null;
  }

  // *** Disabled conditions
  const isReserveTicketsToRequest = reserveGuests.filter((guest) => {
    const invitation = guestInvitation(guest?.guest?.guestId);
    const status = getInvitationStatus(invitation);
    return !status || Number(invitation?.nbrOfRequestedSeats) < guest.tickets;
  }).length;

  const isTicketsToRequest = groupGuests.reduce((acc, curr) => {
    const invitation = guestInvitation(curr?.guest?.guestId);
    if (
      (!invitation?.numberOfNonAllocatedSeats &&
        getInvitationStatus(invitation) ===
          InvitationStatusEnum.BEING_REVIEWED &&
        invitation?.nbrOfRequestedSeats &&
        invitation?.nbrOfRequestedSeats < curr.tickets) ||
      invitation?.importance === ImportanceEnum.OPTIONAL
    ) {
      acc =
        acc ||
        !!(
          curr.tickets - invitation?.nbrOfRequestedSeats ||
          invitation.importance === ImportanceEnum.OPTIONAL
        );
    }

    if (
      invitation?.numberOfNonAllocatedSeats &&
      invitation?.numberOfNonAllocatedSeats < curr.tickets
    ) {
      acc = acc || !!(curr.tickets - invitation?.numberOfNonAllocatedSeats);
    }

    const status = getInvitationStatus(invitation);

    if (!status) {
      acc = acc || !!curr.tickets;
    }

    return acc;
  }, false);

  const allocatedInv = guestInvitation(
    allocatedInvitation?.guest.guestId ?? ''
  );

  const disabledChangeTickets =
    allocatedInv &&
    (allocatedInv?.position?.status === WLPositionStatus.HAS_SEAT ||
      (allocatedInv?.status === 'APPROVED' &&
        (!allocatedInv?.position ||
          allocatedInv?.position?.seatsRequestStatus === 'DECLINED')) ||
      allocatedInv?.status === 'REJECTED' ||
      allocatedInv?.status === 'CANCELLED');
  // ***

  const groupId = waitingList?.groupId ?? '';

  return (
    <>
      <InvitationListTable
        groupGuests={groupGuests}
        guestInvitation={guestInvitation}
        allocatedInvitation={allocatedInvitation}
        totalTickets={totalTickets}
        totalTicketsToIgnore={totalTicketsToIgnore}
        nonAllocatedTickets={nonAllocatedTickets}
        nonAllocatedTicketsWithOptional={nonAllocatedTicketsWithOptional}
        maxTicketsPerHost={maxTicketsPerHost}
        handleRemoveGuest={handleRemoveGuest}
        handleTotalTickets={handleTotalTickets}
        handleTotalTicketsSubmit={handleTotalTicketsSubmit}
        host={host}
        waitingList={waitingList}
        handleDeclineInvitation={handleDeclineInvitation}
        confirmAcceptInvitation={confirmAcceptInvitation}
        showEditModal={showEditModal}
        showChangeTickets={showChangeTickets}
        hideChangeTickets={hideChangeTickets}
        showBusinessReasonModal={showBusinessReasonModal}
        setTickets={setTickets}
        totalTicketsChange={totalTicketsChange}
        businessReason={businessReason}
        setBusinessReason={setBusinessReason}
        disabledChangeTickets={disabledChangeTickets}
        invitations={invitations}
        isFetching={isFetching}
        assignationMode={assignationMode}
        renderFeedbackCell={renderFeedbackCell}
        renderBudgetCell={renderBudgetCell}
      />
      {checkoutModalOpened && selectedGuest && (
        <CheckoutModalHost
          open={checkoutModalOpened}
          onCancel={() => setCheckoutModalOpened(false)}
          selectedGuest={selectedGuest}
          surveyInstances={surveyInstances}
          closeModal={() => setCheckoutModalOpened(false)}
        />
      )}
      {ReactDOM.createPortal(
        <AntFooter
          style={{
            backgroundColor: token.colorBgBase,
            width: '100%',
            padding: '16px 5%',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '20px',
            borderTop: `1px solid ${token.colorBorder}`,
          }}
        >
          <Space
            direction={screens.xs ? 'vertical' : 'horizontal'}
            style={{ justifyContent: 'space-between', width: '100%' }}
          >
            <Space>
              <Space>
                <Text style={{ fontSize: '20px' }} strong>
                  {getRemainingTickets()}
                </Text>
                <Text style={{ fontSize: screens.xs ? '14px' : '20px' }}>
                  {t('in_guestlist')}
                </Text>
              </Space>
              <Divider type="vertical" />
              <Space>
                <Text style={{ fontSize: '20px' }} strong>
                  {reserveListTotal}
                </Text>
                <Text style={{ fontSize: screens.xs ? '14px' : '20px' }}>
                  {t('in_reserve')}
                </Text>
              </Space>
            </Space>
            <Button
              style={{ width: '100%' }}
              size="large"
              type="primary"
              onClick={handleRequest}
              disabled={!isTicketsToRequest && !isReserveTicketsToRequest}
            >
              {t('guestlist_confirm_selection')}
            </Button>
          </Space>
        </AntFooter>,
        document.getElementById('portalForFooter') as HTMLElement
      )}
      <Modal
        title={t('guestlist_confirm_header')}
        open={businessReasonModal}
        onOk={form.submit}
        onCancel={hideBusinessReasonModal}
        okText={t('button_text_confirm')}
      >
        <Form
          initialValues={{
            businessReason,
          }}
          form={form}
          layout="vertical"
          onFinish={(v) => {
            setBusinessReason(v.businessReason);
            hideBusinessReasonModal();
          }}
        >
          <Form.Item
            label={t('guestlist_business_reason')}
            name="businessReason"
            rules={[
              { required: true, message: 'Please input business reason' },
            ]}
          >
            <TextArea
              showCount
              maxLength={150}
              style={{ height: 120, resize: 'none' }}
              placeholder={t('guestlist_business_reason') as string}
            />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title={t('guestlist_business_reason')}
        open={emptyBusinessReasonModal}
        onOk={() => {
          setEmptyBusinessReasonModal(false);
          showBusinessReasonModal();
        }}
        onCancel={() => setEmptyBusinessReasonModal(false)}
        okText={t('guestlist_business_reason_missing-btn')}
      >
        <Text>
          {t('guestlist_business_reason_missing', {
            nonallocated: getRemainingTickets(),
          })}
        </Text>
      </Modal>
      <Modal
        title={t('guestlist_confirm_header')}
        open={confirmationModal}
        onCancel={() => setConfirmationModal(false)}
        footer={[
          <Space key="footer-actions">
            <Button type="text" onClick={handlePublicOfficialModal}>
              {t('guestlist_public_official_add-btn')}
            </Button>
            <Button key="back" onClick={() => setConfirmationModal(false)}>
              {t('button_text_cancel')}
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={loading}
              onClick={() => submitRequest()}
            >
              {t('button_text_submit')}
            </Button>
          </Space>,
        ]}
      >
        <Space direction="vertical">
          <Text>{t('guestlist_confirm_phrase')}</Text>
          <Text type="secondary">{t('public_official_action')}</Text>
        </Space>
      </Modal>
      <InternalEmployeeModal
        specialGuestModal={specialGuestModal}
        setSpecialGuestModal={setSpecialGuestModal}
        onSubmitForm={submitRequest}
      />
      <PublicOfficialModal
        waitingListId={waitingListId}
        isOpened={publicOfficialModal}
        onClose={() => setPublicOfficialModal(false)}
      />
      {isEditContactModal && (
        <EditContactModal
          groupId={groupId}
          isEditContactModal={isEditContactModal}
          setIsEditContactModal={setIsEditContactModal}
          guestToEdit={guestToEdit}
          setGuestToEdit={setGuestToEdit}
          isInvitationExists
        />
      )}
    </>
  );
}
